import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { getAuth } from "firebase/auth";
import { app } from "./firebase";
import BackButton from "./BackButton";
import { useNavigate, Link } from "react-router-dom";
import { EnvironmentManager, buildUrl } from "../config/EnvironmentManager";
import axios from "axios";


import "./Profile.css";
const BASE_URL = EnvironmentManager.baseUrl;

const Profile = () => {
  const { userId, userEmail, currentUser: contextUser,currentUser, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [viewResumeLoading, setViewResumeLoading] = useState(false);
  const [downloadResumeLoading, setDownloadResumeLoading] = useState(false);
  
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const [resumeDropdownOpen, setResumeDropdownOpen] = useState(false);
  const [userMenuDropdownOpen, setUserMenuDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const profileDropdownRef = useRef(null);
  const addressDropdownRef = useRef(null);
  const resumeDropdownRef = useRef(null);
  const userMenuDropdownRef = useRef(null);

  const [activeModal, setActiveModal] = useState(null);
  const [activeSection, setActiveSection] = useState('combined');
  const [message, setMessage] = useState({ type: '', text: '' });

  
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    phoneNo: "",
    emailId: "",
    mainProfession: "",
    otherProfessions: "",
    experience: "",
    noticePeriod: "",
    openToWork: false,
  });

  const [addressForm, setAddressForm] = useState({
    presentAddress: {
      houseNo: "",
      roadName: "",
      area: "",
      district: "",
      state: "",
      pincode: ""
    },
    permanentAddress: {
      houseNo: "",
      roadName: "",
      area: "",
      district: "",
      state: "",
      pincode: ""
    }
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [resumeDocumentId, setResumeDocumentId] = useState("");

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

 
  const [userProfile, setUserProfile] = useState(null);
  const [userAddress, setUserAddress] = useState(null);
  const [userResume, setUserResume] = useState(null);
  const [loading, setLoading] = useState(false);

  const user = contextUser;

  useEffect(() => {
    if (userId && !authLoading) {
      loadAllData();
    }
  }, [userId, authLoading]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (addressDropdownRef.current && !addressDropdownRef.current.contains(event.target)) {
        setAddressDropdownOpen(false);
      }
      if (resumeDropdownRef.current && !resumeDropdownRef.current.contains(event.target)) {
        setResumeDropdownOpen(false);
      }
      if (userMenuDropdownRef.current && !userMenuDropdownRef.current.contains(event.target)) {
        setUserMenuDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // File upload handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelection = (file) => {
    
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      showMessage('error', 'Please upload a PDF or Word document (PDF, DOC, DOCX)');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', 'File size should be less than 5MB');
      return;
    }
    
    setResumeFile(file);
    setUploadProgress(0);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const removeFile = () => {
    setResumeFile(null);
    setUploadProgress(0);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return '📄';
    
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return '';
      case 'doc':
      case 'docx':
        return '';
      default:
        return '📄';
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const getIdToken = async () => {
    const auth = getAuth(app);
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken(true);
    } else {
      throw new Error("No user is logged in");
    }
  };

  const loadAllData = async () => {
    try {
      await Promise.all([
        loadUserProfile(),
        loadUserAddress(),
        loadResumeData()
      ]);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  
  const loadUserProfile = async () => {
    try {
      const idToken = await getIdToken();
      const url = buildUrl(EnvironmentManager.endpoints.getUserDetails, userId);
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUserProfile(userData);
      } else if (response.status === 404) {
        setUserProfile(null);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error loading user profile:", error);
      setUserProfile(null);
    }
    axios
      .delete(
        `${BASE_URL}/profile-resume-document/delete-profile-resume-document/${resumeDocumentId}`,
        { headers: { "TokenType": "developer" } }
      )
      .then((res) => {
        console.log("Resume Delete Response:", res.data);
        alert("Resume deleted successfully!");
        setResumeDocumentId("");
      })
      .catch((err) => console.error("Error deleting resume:", err));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const idToken = await getIdToken();
      const payload = {
        userId: userId,
        firstName: profileForm.firstName,
        middleName: profileForm.middleName,
        lastName: profileForm.lastName,
        phoneNo: profileForm.phoneNo,
        emailId: profileForm.emailId || userEmail,
        mainProfession: profileForm.mainProfession,
        otherProfessions: profileForm.otherProfessions || "Nx",
        experience: parseFloat(profileForm.experience) || 0,
        noticePeriod: parseInt(profileForm.noticePeriod) || 0,
        openToWork: profileForm.openToWork || false,
      };

      let url;
      let method;

      if (userProfile) {
        url = buildUrl(EnvironmentManager.endpoints.updateUserDetails, userId);
        method = "POST";
      } else {
        url = buildUrl(EnvironmentManager.endpoints.saveUserDetails);
        method = "POST";
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      console.log('Upload successful, response:', response.data);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      await loadUserProfile();
      setActiveModal(null);
      showMessage('success', 'Profile saved successfully!');
      
    } catch (error) {
      console.error("Error saving profile:", error);
      showMessage('error', `Error saving profile: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!window.confirm("Are you sure you want to delete your profile? This action cannot be undone.")) return;
    
    setLoading(true);
    try {
      const idToken = await getIdToken();
      const url = buildUrl(EnvironmentManager.endpoints.deleteUserDetails, userId);
      
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      setUserProfile(null);
      setProfileDropdownOpen(false);
      showMessage('success', 'Profile deleted successfully!');
    } catch (error) {
      console.error("Error deleting profile:", error);
      showMessage('error', `Error deleting profile: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  
  const loadUserAddress = async () => {
    try {
      const idToken = await getIdToken();
      const url = buildUrl(EnvironmentManager.endpoints.getUserAddress, userId);
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const addressData = await response.json();
        setUserAddress(addressData);
      } else if (response.status === 404) {
        setUserAddress(null);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error loading user address:", error);
      setUserAddress(null);
    }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const idToken = await getIdToken();
      const payload = {
        userId: userId,
        presentAddress: addressForm.presentAddress,
        permanentAddress: addressForm.permanentAddress
      };

      let url;
      let method;

      if (userAddress) {
        url = buildUrl(EnvironmentManager.endpoints.updateUserAddress, userId);
        method = "POST";
      } else {
        url = buildUrl(EnvironmentManager.endpoints.saveUserAddress);
        method = "POST";
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      await loadUserAddress();
      setActiveModal(null);
      showMessage('success', 'Address saved successfully!');
      
    } catch (error) {
      console.error("Error saving address:", error);
      showMessage('error', `Error saving address: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async () => {
    if (!window.confirm("Are you sure you want to delete your address? This action cannot be undone.")) return;
    
    setLoading(true);
    try {
      const idToken = await getIdToken();
      const url = buildUrl(EnvironmentManager.endpoints.deleteUserAddress, userId);
      
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      setUserAddress(null);
      setAddressDropdownOpen(false);
      showMessage('success', 'Address deleted successfully!');
    } catch (error) {
      console.error("Error deleting address:", error);
      showMessage('error', `Error deleting address: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Resume CRUD Operations
  const loadResumeData = async () => {
    try {
      const idToken = await getIdToken();
      const url = buildUrl(EnvironmentManager.endpoints.getResumeDocument, userId);
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const resumeData = await response.json();
        setUserResume(resumeData);
      } else if (response.status === 404) {
        setUserResume(null);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error loading resume data:", error);
      setUserResume(null);
    }
  };

  const handleUploadResume = async () => {
    if (!resumeFile) {
      showMessage('error', 'Please select a file to upload');
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    
    try {
      const idToken = await getIdToken();
      const formData = new FormData();
      formData.append("file", resumeFile);
      formData.append("userId", userId);
      formData.append("userEmail", userEmail);
      formData.append("fileName", resumeFile.name);

      let url;
      let method;

      if (userResume) {
        url = buildUrl(EnvironmentManager.endpoints.updateResumeDocument, userId);
        method = "POST";
      } else {
        url = buildUrl(EnvironmentManager.endpoints.saveResumeDocument);
        method = "POST";
      }


      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(url, {
        method: method,
        headers: {
          "Authorization": `Bearer ${idToken}`,
        },
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      await loadResumeData();
      setTimeout(() => {
        setActiveModal(null);
        setResumeFile(null);
        setUploadProgress(0);
        showMessage('success', 'Resume uploaded successfully!');
      }, 1000);
      
    } catch (error) {
      console.error("Error uploading resume:", error);
      setUploadProgress(0);
      showMessage('error', `Error uploading resume: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteResume = async () => {
    if (!window.confirm("Are you sure you want to delete your resume? This action cannot be undone.")) return;
    
    setLoading(true);
    try {
      const idToken = await getIdToken();
      const url = buildUrl(EnvironmentManager.endpoints.deleteResumeDocument, userId);
      
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      setUserResume(null);
      setResumeDropdownOpen(false);
      showMessage('success', 'Resume deleted successfully!');
    } catch (error) {
      console.error("Error deleting resume:", error);
      showMessage('error', `Error deleting resume: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadResume = async () => {
    if (downloadResumeLoading) return;
  
  setDownloadResumeLoading(true);
  try {
    
    if (!userResume || !userResume.profileDocUrl) {
      showMessage("error", "Resume not available");
      setDownloadResumeLoading(false);
      return;
    }

    const idToken = await getIdToken();
    const fileUrl = userResume.profileDocUrl; 
    const backendUrl = `${EnvironmentManager.baseUrl}/mv-profile/files/download?fileUrl=${encodeURIComponent(fileUrl)}`;

    const response = await fetch(backendUrl, {
      headers: { "Authorization": `Bearer ${idToken}` }
    });

    if (!response.ok) throw new Error("Failed to download");

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = userResume.fileName || "resume.pdf";
    a.click();

    window.URL.revokeObjectURL(url);
    showMessage("success", "Resume downloaded successfully!");
  } catch (e) {
    console.error(e);
    showMessage("error", "Failed to download resume");
  } finally {
    setDownloadResumeLoading(false);
  }
};
const handleViewResume = async () => {
  if (viewResumeLoading) return;

  setViewResumeLoading(true);
  try {
    
    const fileUrl = getResumeFileUrl();

    if (!fileUrl) {
      showMessage("error", "Resume not available");
      return;
    }

    const idToken = await getIdToken();

    const backendUrl = `${EnvironmentManager.baseUrl}/mv-profile/files/download?fileUrl=${encodeURIComponent(
      fileUrl
    )}`;

    const response = await fetch(backendUrl, {
      headers: { Authorization: `Bearer ${idToken}` }
    });

    if (!response.ok) throw new Error("Failed to load file");

    const blob = await response.blob();
    const fileType = blob.type;

   
    const fileName = userResume?.file?.name || userResume?.fileName || "resume";

    const blobUrl = window.URL.createObjectURL(blob);

  
    if (fileType === "application/pdf") {
      window.open(blobUrl, "_blank");
    } 

    else {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      a.click();
    }

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("View resume error:", error);
    showMessage("error", "Unable to open resume");
  } finally {
    setViewResumeLoading(false);
  }
};
const getResumeFileUrl = () => {
  return (
    userResume?.file?.url ||      
    userResume?.resumeUrl ||     
    userResume?.profileDocUrl || 
    ""
  );
};

  // Modal handlers
  const openModal = (modalType) => {
    setActiveModal(modalType);
    
    if (modalType === 'profile' && userProfile) {
      setProfileForm({
        firstName: userProfile.firstName || "",
        middleName: userProfile.middleName || "",
        lastName: userProfile.lastName || "",
        phoneNo: userProfile.phoneNo || "",
        emailId: userProfile.emailId || userEmail || "",
        mainProfession: userProfile.mainProfession || "",
        otherProfessions: userProfile.otherProfessions || "",
        experience: userProfile.experience?.toString() || "",
        noticePeriod: userProfile.noticePeriod?.toString() || "",
        openToWork: userProfile.openToWork || false,
      });
    } else if (modalType === 'address' && userAddress) {
      setAddressForm({
        presentAddress: userAddress.presentAddress || {
          houseNo: "", roadName: "", area: "", district: "", state: "", pincode: ""
        },
        permanentAddress: userAddress.permanentAddress || {
          houseNo: "", roadName: "", area: "", district: "", state: "", pincode: ""
        }
      });
    } else {
      if (modalType === 'profile') {
        setProfileForm({
          firstName: "", middleName: "", lastName: "", phoneNo: "", emailId: userEmail || "",
          mainProfession: "", otherProfessions: "", experience: "", noticePeriod: "", openToWork: false
        });
      } else if (modalType === 'address') {
        setAddressForm({
          presentAddress: { houseNo: "", roadName: "", area: "", district: "", state: "", pincode: "" },
          permanentAddress: { houseNo: "", roadName: "", area: "", district: "", state: "", pincode: "" }
        });
      }
    }
  };

  const closeModal = () => {
    setActiveModal(null);
    setResumeFile(null);
    setUploadProgress(0);
    setIsDragging(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/mv-profile');
    } catch (error) {
      console.error('Logout error:', error);
      showMessage('error', 'Error during logout');
    }
  };
  const getUserInitial = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName[0].toUpperCase();
    } else if (currentUser?.email) {
      return currentUser.email[0].toUpperCase();
    }
    return 'U';
  };

  if (authLoading) {
    return (
      <div className="profile-container">
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-container">
        <nav className="navbar">
          <div className="nav-brand">MV Profile</div>
          <div className="nav-links">
            <a href="/mv-profile" className="nav-link">Home</a>
            <a href="/contact" className="nav-link">Contact Us</a>
            <a href="/login" className="nav-link">Login</a>
          </div>
        </nav>
        <div className="login-prompt">
          <h2>Access Denied</h2>
          <p>Please log in to access your profile.</p>
          <a href="/login" className="btn-primary">Go to Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* Navigation */}

      
    
  
      <header className="nav-header">
        <nav className="nav-content">
          <div className="nav-left">
  <button className="back-btn" onClick={() => navigate(-1)}>←Back</button>
  <div className="nav-brand">
    <Link to="/">MV Profile</Link>
  </div>
</div>

          <ul className="nav-list">
            <li><Link to="/MHMV-profile/job-search">Find Jobs</Link></li>
            <li><Link to="/MHMV-profile/job-posting">Post a Free Job</Link></li>
            <li className="dropdown">
              <Link to="#" className="dropbtn">Courses</Link>
              <div className="dropdown-content">
                <Link to="/MHMV-profile/audio-courses/admin">Audio Courses</Link>
                <Link to="/MHMV-profile/video-courses">Video Courses</Link>
              </div>
            </li>
            <li className="dropdown">
              <Link to="#" className="dropbtn">Services</Link>
              <div className="dropdown-content">
                <Link to="/android-dev">Android Development</Link>
                <Link to="/custom-software">Custom Software Development</Link>
                <Link to="/it-consulting">IT Consulting</Link>
                <Link to="/web-dev">Web Development</Link>
                <Link to="/mobile-app">Mobile Application Development</Link>
                <Link to="/training">Training</Link>
                <Link to="/career-dev">Career Development Coaching</Link>
                <Link to="/corporate-training">Corporate Training</Link>
                <Link to="/cloud-app">Cloud Application Development</Link>
              </div>
            </li>
            
            
            {!currentUser ? (
              
              <>
                <li><Link to="/MHMV-profile/jobseeker-login">Job Seeker Login</Link></li>
                <li><Link to="/MHMV-profile/employee-login">Employee Login</Link></li>
              </>
            ) : (
              
              <li className="dropdown">
                <Link to="#" className="dropbtn user-dropdown">
                  <div className="user-avatar-small">
                    {getUserInitial()}
                  </div>
                </Link>
                <div className="dropdown-content user-dropdown-content">
                  <Link to="/MHMV-profile/profile">View Profile</Link>
                  <Link to="/settings">Settings</Link>
                  <button onClick={handleLogout} className="logout-btn">Logout</button>
                </div>
              </li>
            )}
          </ul>
          <button
      className="menu-toggle"
      onClick={() => setIsMenuOpen(!isMenuOpen)}
    >
      ☰
    </button>
        </nav>
      </header>
      
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      
      

      <div className="profile-content">
        {/* Sidebar */}
        <div className="sidebar">
          <button 
            className={`sidebar-item ${activeSection === 'combined' ? 'active' : ''}`}
            onClick={() => setActiveSection('combined')}
          >
            Combined Profile
          </button>
          <button 
            className={`sidebar-item ${activeSection === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveSection('profile')}
          >
            Personal Details
          </button>
          <button 
            className={`sidebar-item ${activeSection === 'address' ? 'active' : ''}`}
            onClick={() => setActiveSection('address')}
          >
            Address
          </button>
          <button 
            className={`sidebar-item ${activeSection === 'resume' ? 'active' : ''}`}
            onClick={() => setActiveSection('resume')}
          >
            Resume
          </button>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Combined Profile View */}
          {activeSection === 'combined' && (
            <div className="content-section">
              <div className="combined-profile-header">
                <div className="profile-avatar-large">
                  {userProfile?.firstName?.[0]?.toUpperCase() || userEmail?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="profile-info">
                  <h1>
                    {userProfile ? `${userProfile.firstName} ${userProfile.lastName}`.trim() : 'Complete Your Profile'}
                  </h1>
                  <p className="profile-email">{userEmail}</p>
                  {userProfile?.mainProfession && (
                    <p className="profile-profession">{userProfile.mainProfession}</p>
                  )}
                  <div className="open-to-work-badge">
                    {userProfile?.isOpenToWork && 'Open to Work'}
                    <p className="open-to-work-badge">Open to Work</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button 
                  className="btn-primary"
                  onClick={() => openModal('profile')}
                >
                  {userProfile ? 'Edit Profile' : 'Add Profile Details'}
                </button>
                <button 
                  className="btn-primary"
                  onClick={() => openModal('address')}
                >
                  {userAddress ? 'Edit Address' : 'Add Address'}
                </button>
                <button 
                  className="btn-primary"
                  onClick={() => openModal('resume')}
                >
                  {userResume ? 'Update Resume' : 'Add Resume'}
                </button>
              </div>

              {/* Profile Details Summary */}
              <div className="profile-sections">
                {/* Personal Information */}
                {userProfile && (
                  <div className="profile-section">
                    <div className="section-header">
                      <h3>Personal Information</h3>
                      <div className="section-actions">
                        <button 
                          className="btn-options"
                          onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                        >
                          ⋮
                        </button>
                        {profileDropdownOpen && (
                          <div className="dropdown-menu" ref={profileDropdownRef}>
                            <button onClick={() => {
                              openModal('profile');
                              setProfileDropdownOpen(false);
                            }}>Edit</button>
                            <button onClick={handleDeleteProfile}>Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="section-content">
                      <div className="info-grid">
                        <div className="info-item">
                          <strong>Name:</strong> {`${userProfile.firstName} ${userProfile.middleName || ''} ${userProfile.lastName}`.trim()}
                        </div>
                        <div className="info-item">
                          <strong>Phone:</strong> {userProfile.phoneNo}
                        </div>
                        <div className="info-item">
                          <strong>Email:</strong> {userProfile.emailId}
                        </div>
                        <div className="info-item">
                          <strong>Profession:</strong> {userProfile.mainProfession}
                        </div>
                        <div className="info-item">
                          <strong>Other Professions:</strong> {userProfile.otherProfessions}
                        </div>
                        <div className="info-item">
                          <strong>Experience:</strong> {userProfile.experience} years
                        </div>
                        <div className="info-item">
                          <strong>Notice Period:</strong> {userProfile.noticePeriod} days
                        </div>
                        <div className="info-item">
                          <strong>Status:</strong> {userProfile.openToWork ? 'Open to Work' : 'Not Available'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Address Information */}
{userAddress && (
  <div className="profile-section">
    <div className="section-header">
      <h3>Address Information</h3>
      <div className="section-actions">
        <button 
          className="btn-options"
          onClick={() => setAddressDropdownOpen(!addressDropdownOpen)}
        >
          ⋮
        </button>
        {addressDropdownOpen && (
          <div className="dropdown-menu" ref={addressDropdownRef}>
            <button onClick={() => {
              openModal('address');
              setAddressDropdownOpen(false);
            }}>Edit</button>
            <button onClick={handleDeleteAddress}>Delete</button>
          </div>
        )}
      </div>
    </div>
    <div className="section-content">
      <div className="address-grid">
        <div className="address-type">
          <h4>Present Address</h4>
          <div className="info-item">
            <strong>Address:</strong> {userAddress.presentAddress.houseNo}, {userAddress.presentAddress.roadName}
          </div>
          <div className="info-item">
            <strong>Area:</strong> {userAddress.presentAddress.area}, {userAddress.presentAddress.district}
          </div>
          <div className="info-item">
            <strong>State & Pin:</strong> {userAddress.presentAddress.state} - {userAddress.presentAddress.pincode}
          </div>
        </div>
        <div className="address-type">
          <h4>Permanent Address</h4>
          <div className="info-item">
            <strong>Address:</strong> {userAddress.permanentAddress.houseNo}, {userAddress.permanentAddress.roadName}
          </div>
          <div className="info-item">
            <strong>Area:</strong> {userAddress.permanentAddress.area}, {userAddress.permanentAddress.district}
          </div>
          <div className="info-item">
            <strong>State & Pin:</strong> {userAddress.permanentAddress.state} - {userAddress.permanentAddress.pincode}
          </div>
        </div>
      </div>
    </div>
  </div>
)}

                {/* Resume Information */}
                {userResume && (
                  <div className="profile-section">
                    <div className="section-header">
                      <h3>Resume</h3>
                      <div className="section-actions">
                        <button 
                          className="btn-options"
                          onClick={() => setResumeDropdownOpen(!resumeDropdownOpen)}
                        >
                          ⋮
                        </button>
                        {resumeDropdownOpen && (
                          <div className="dropdown-menu" ref={resumeDropdownRef}>
                            <button onClick={() => {
                              openModal('resume');
                              setResumeDropdownOpen(false);
                            }}>Update</button>
                            <button onClick={handleDownloadResume}>Download</button>
                            <button onClick={handleDeleteResume}>Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="section-content">
                      <div className="resume-preview">
                        <div className="resume-file-card">
                          <div className="file-icon-large">
                            {getFileIcon(userResume.fileName)}
                          </div>
                          <div className="file-details">
                            <h4 className="file-name">{userResume.fileName}</h4>
                            <div className="file-meta">
                              <span className="file-size">{formatFileSize(userResume.fileSize || 0)}</span>
                              <span className="file-type">{userResume.fileType || 'Document'}</span>
                              <span className="upload-date">
                                Uploaded: {new Date(userResume.uploadDate).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="file-actions">
                            
                            <button className="btn-view" onClick={handleViewResume}>
                              View Resume
                            </button>
                            <button className="btn-download" onClick={handleDownloadResume}>
                              Download
                            </button>
                                                    </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Empty States */}
                {!userProfile && !userAddress && !userResume && (
                  <div className="empty-state">
                    <p>No profile data added yet. Use the buttons above to get started!</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Individual Section Views */}
          {activeSection !== 'combined' && (
            <div className="content-section">
              <div className="section-header">
                <h2>
                  {activeSection === 'profile' && 'Personal Details'}
                  {activeSection === 'address' && 'Address Information'}
                  {activeSection === 'resume' && 'Resume Management'}
                </h2>
                <button 
                  className="btn-primary"
                  onClick={() => openModal(activeSection)}
                >
                  {activeSection === 'profile' && (userProfile ? 'Edit Profile' : 'Add Profile')}
                  {activeSection === 'address' && (userAddress ? 'Edit Address' : 'Add Address')}
                  {activeSection === 'resume' && (userResume ? 'Update Resume' : 'Upload Resume')}
                </button>
              </div>

              {/* Section-specific content */}
              <div className="section-content">
                {activeSection === 'profile' && (
                  userProfile ? (
                    <div className="info-grid">
                      <div className="info-item"><strong>First Name:</strong> {userProfile.firstName}</div>
                      <div className="info-item"><strong>Last Name:</strong> {userProfile.lastName}</div>
                      <div className="info-item"><strong>Phone:</strong> {userProfile.phoneNo}</div>
                      <div className="info-item"><strong>Email:</strong> {userProfile.emailId}</div>
                      <div className="info-item"><strong>Profession:</strong> {userProfile.mainProfession}</div>
                      <div className="info-item"><strong>Experience:</strong> {userProfile.experience} years</div>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No profile information added yet.</p>
                    </div>
                  )
                )}

                {activeSection === 'address' && (
                  userAddress ? (
                    <div className="address-grid">
                      <div className="address-type">
                        <h4>Present Address</h4>
                        <p>{userAddress.presentAddress.houseNo}, {userAddress.presentAddress.roadName}</p>
                        <p>{userAddress.presentAddress.area}, {userAddress.presentAddress.district}</p>
                        <p>{userAddress.presentAddress.state} - {userAddress.presentAddress.pincode}</p>
                      </div>
                      <div className="address-type">
                        <h4>Permanent Address</h4>
                        <p>{userAddress.permanentAddress.houseNo}, {userAddress.permanentAddress.roadName}</p>
                        <p>{userAddress.permanentAddress.area}, {userAddress.permanentAddress.district}</p>
                        <p>{userAddress.permanentAddress.state} - {userAddress.permanentAddress.pincode}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No address information added yet.</p>
                    </div>
                  )
                )}

                {activeSection === 'resume' && (
                  userResume ? (
                    <div className="resume-preview">
                      <div className="resume-file-card">
                        <div className="file-icon-large">
                          {getFileIcon(userResume.fileName)}
                        </div>
                        <div className="file-details">
                          <h4 className="file-name">{userResume.fileName}</h4>
                          <div className="file-meta">
                            <span className="file-size">{formatFileSize(userResume.fileSize || 0)}</span>
                            <span className="file-type">{userResume.fileType || 'Document'}</span>
                            <span className="upload-date">
                              Uploaded: {new Date(userResume.uploadDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="file-actions">
                          <button className="btn-download" onClick={handleDownloadResume}>
                            Download
                          </button>
                          <button className="btn-danger" onClick={handleDeleteResume}>
                            Delete
                          </button>
                          <button className="btn-view" onClick={handleViewResume}>
                            View Resume
                          </button>
                          
                          
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No resume uploaded yet.</p>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {activeModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {activeModal === 'profile' && (userProfile ? 'Edit Profile' : 'Add Profile Details')}
                {activeModal === 'address' && (userAddress ? 'Edit Address' : 'Add Address')}
                {activeModal === 'resume' && (userResume ? 'Update Resume' : 'Upload Resume')}
              </h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="modal-body">
              {/* Profile Modal */}
              {activeModal === 'profile' && (
                <form onSubmit={handleSaveProfile} className="profile-form">
                  <div className="form-columns">
                    <div className="form-column">
                      <div className="form-group">
                        <label className="form-label">First Name *</label>
                        <input
                          type="text"
                          value={profileForm.firstName}
                          onChange={(e) => setProfileForm({...profileForm, firstName: e.target.value})}
                          required
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Middle Name</label>
                        <input
                          type="text"
                          value={profileForm.middleName}
                          onChange={(e) => setProfileForm({...profileForm, middleName: e.target.value})}
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Last Name *</label>
                        <input
                          type="text"
                          value={profileForm.lastName}
                          onChange={(e) => setProfileForm({...profileForm, lastName: e.target.value})}
                          required
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="form-column">
                      <div className="form-group">
                        <label className="form-label">Phone Number *</label>
                        <input
                          type="tel"
                          value={profileForm.phoneNo}
                          onChange={(e) => setProfileForm({...profileForm, phoneNo: e.target.value})}
                          required
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email *</label>
                        <input
                          type="email"
                          value={profileForm.emailId}
                          onChange={(e) => setProfileForm({...profileForm, emailId: e.target.value})}
                          required
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Main Profession *</label>
                        <input
                          type="text"
                          value={profileForm.mainProfession}
                          onChange={(e) => setProfileForm({...profileForm, mainProfession: e.target.value})}
                          required
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="form-column">
                      <div className="form-group">
                        <label className="form-label">Other Professions</label>
                        <input
                          type="text"
                          value={profileForm.otherProfessions}
                          onChange={(e) => setProfileForm({...profileForm, otherProfessions: e.target.value})}
                          className="form-input"
                          placeholder="e.g., Freelancer, Blogger"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Experience (years) *</label>
                        <input
                          type="number"
                          value={profileForm.experience}
                          onChange={(e) => setProfileForm({...profileForm, experience: e.target.value})}
                          required
                          min="0"
                          step="0.5"
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Notice Period (days)</label>
                        <input
                          type="number"
                          value={profileForm.noticePeriod}
                          onChange={(e) => setProfileForm({...profileForm, noticePeriod: e.target.value})}
                          min="0"
                          className="form-input"
                        />
                      </div>
                      {/* Checkbox Section - Explicit styling */}
<div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
    <input
      type="checkbox"
      id="openToWork"
      checked={profileForm.openToWork}
      onChange={(e) => setProfileForm({...profileForm, openToWork: e.target.checked})}
      style={{ 
        width: '18px', 
        height: '18px', 
        margin: '0',
        cursor: 'pointer'
      }}
    />
    <label 
      htmlFor="openToWork" 
      style={{ 
        margin: '0', 
        cursor: 'pointer',
        fontWeight: '500',
        color: '#333'
      }}
    >
      Open to Work Opportunities
    </label>
  </div>
</div>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
                    <button type="submit" disabled={loading} className="btn-primary">
                      {loading ? 'Saving...' : (userProfile ? 'Update Profile' : 'Save Profile')}
                    </button>
                  </div>
                </form>
              )}

              {/* Address Modal */}
              {activeModal === 'address' && (
                <form onSubmit={handleSaveAddress} className="profile-form">
                  <div className="address-sections">
                    <div className="address-section">
                      <h3>Present Address</h3>
                      <div className="form-columns">
                        <div className="form-column">
                          <div className="form-group">
                            <label className="form-label">House No *</label>
                            <input
                              type="text"
                              value={addressForm.presentAddress.houseNo}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                presentAddress: {...addressForm.presentAddress, houseNo: e.target.value}
                              })}
                              required
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Road Name *</label>
                            <input
                              type="text"
                              value={addressForm.presentAddress.roadName}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                presentAddress: {...addressForm.presentAddress, roadName: e.target.value}
                              })}
                              required
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Area</label>
                            <input
                              type="text"
                              value={addressForm.presentAddress.area}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                presentAddress: {...addressForm.presentAddress, area: e.target.value}
                              })}
                              className="form-input"
                            />
                          </div>
                        </div>
                        <div className="form-column">
                          <div className="form-group">
                            <label className="form-label">District *</label>
                            <input
                              type="text"
                              value={addressForm.presentAddress.district}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                presentAddress: {...addressForm.presentAddress, district: e.target.value}
                              })}
                              required
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">State *</label>
                            <input
                              type="text"
                              value={addressForm.presentAddress.state}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                presentAddress: {...addressForm.presentAddress, state: e.target.value}
                              })}
                              required
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Pin Code *</label>
                            <input
                              type="text"
                              value={addressForm.presentAddress.pincode}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                presentAddress: {...addressForm.presentAddress, pincode: e.target.value}
                              })}
                              required
                              className="form-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="address-section">
                      <h3>Permanent Address</h3>
                      <div className="form-checkbox">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAddressForm({
                                  ...addressForm,
                                  permanentAddress: {...addressForm.presentAddress}
                                });
                              } else {
                                setAddressForm({
                                  ...addressForm,
                                  permanentAddress: {
                                    houseNo: "", roadName: "", area: "", district: "", state: "", pincode: ""
                                  }
                                });
                              }
                            }}
                            className="checkbox-input"
                          />
                          <span className="checkbox-custom"></span>
                          Same as Present Address
                        </label>
                      </div>
                      <div className="form-columns">
                        <div className="form-column">
                          <div className="form-group">
                            <label className="form-label">House No</label>
                            <input
                              type="text"
                              value={addressForm.permanentAddress.houseNo}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                permanentAddress: {...addressForm.permanentAddress, houseNo: e.target.value}
                              })}
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Road Name</label>
                            <input
                              type="text"
                              value={addressForm.permanentAddress.roadName}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                permanentAddress: {...addressForm.permanentAddress, roadName: e.target.value}
                              })}
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Area</label>
                            <input
                              type="text"
                              value={addressForm.permanentAddress.area}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                permanentAddress: {...addressForm.permanentAddress, area: e.target.value}
                              })}
                              className="form-input"
                            />
                          </div>
                        </div>
                        <div className="form-column">
                          <div className="form-group">
                            <label className="form-label">District</label>
                            <input
                              type="text"
                              value={addressForm.permanentAddress.district}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                permanentAddress: {...addressForm.permanentAddress, district: e.target.value}
                              })}
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">State</label>
                            <input
                              type="text"
                              value={addressForm.permanentAddress.state}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                permanentAddress: {...addressForm.permanentAddress, state: e.target.value}
                              })}
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Pin Code</label>
                            <input
                              type="text"
                              value={addressForm.permanentAddress.pincode}
                              onChange={(e) => setAddressForm({
                                ...addressForm,
                                permanentAddress: {...addressForm.permanentAddress, pincode: e.target.value}
                              })}
                              className="form-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
                    <button type="submit" disabled={loading} className="btn-primary">
                      {loading ? 'Saving...' : (userAddress ? 'Update Address' : 'Save Address')}
                    </button>
                  </div>
                </form>
              )}

              {/* Resume Modal */}
              {activeModal === 'resume' && (
                <div className="resume-upload-modal">
                  {/* File Upload Area */}
                  <div 
                    className={`file-upload-area ${isDragging ? 'dragging' : ''} ${resumeFile ? 'has-file' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileInput}
                      className="file-input"
                      id="resume-upload"
                    />
                    
                    {!resumeFile ? (
                      <label htmlFor="resume-upload" className="file-upload-label">
                        <div className="upload-icon">📎</div>
                        <div className="upload-text">
                          <h4>Upload your resume</h4>
                          <p>Drag and drop your file here or click to browse</p>
                        </div>
                        <div className="upload-requirements">
                          <small>Supported formats: PDF, DOC, DOCX</small>
                          <small>Max file size: 5MB</small>
                        </div>
                      </label>
                    ) : (
                      <div className="file-preview">
                        <div className="file-preview-header">
                          <h4>File Preview</h4>
                          <button className="btn-remove" onClick={removeFile}>×</button>
                        </div>
                        <div className="file-preview-content">
                          <div className="file-icon-preview">
                            {getFileIcon(resumeFile.name)}
                          </div>
                          <div className="file-info">
                            <h5 className="file-name">{resumeFile.name}</h5>
                            <div className="file-details">
                              <span className="file-size">{formatFileSize(resumeFile.size)}</span>
                              <span className="file-type">{resumeFile.type.split('/')[1]?.toUpperCase()}</span>
                              <span className="file-modified">
                                Modified: {new Date(resumeFile.lastModified).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Upload Progress */}
                        {uploadProgress > 0 && (
                          <div className="upload-progress">
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{ width: `${uploadProgress}%` }}
                              ></div>
                            </div>
                            <span className="progress-text">{uploadProgress}%</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
                    <button 
                      onClick={handleUploadResume} 
                      disabled={loading || !resumeFile || uploadProgress > 0}
                      className="btn-primary"
                    >
                      {loading ? 'Uploading...' : (userResume ? 'Update Resume' : 'Upload Resume')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
